// MUST be first import — sets __webpack_public_path__ from window.environment
// before webpack loads any async chunk.
import "./publicPath";
import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { persistor, store } from "./store";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance, initializeMsal } from "./services/msalService";
import { configureAmplify } from "./config/amplifyConfig";
import { registerCognitoHubListener } from "./services/cognitoBootstrap";
import { getThemeName, loadTheme } from "./config/themeConfig";

// Set theme CSS variables on <html> BEFORE render so routes that don't mount
// AppContent (e.g. /login) still pick up theme-derived tokens like
// --secondary-color-opacity-8 and --neutral-grey-darker. AppContent's effect
// re-runs loadTheme() after mount to keep Redux theme state in sync — that's
// idempotent against this pre-render call.
loadTheme(getThemeName());

// Configure Amplify and register the Hub listener BEFORE render so the auto
// `?code=` exchange (Amplify v6 detects the OAuth callback on first import)
// and any in-flight refresh see a fully wired session. Both are idempotent
// and no-op when REACT_APP_IDP_TYPE is not "cognito".
configureAmplify();
registerCognitoHubListener();

// Kick off MSAL initialization immediately so it overlaps with React mount + bundle parse.
// Without this, the axios interceptor's first request blocks on a cold init (~200–400 ms).
initializeMsal().catch(() => {
	// swallowed — msalService logs + resets state; app still renders and will retry on demand.
});

const rootElement = document.getElementById("root");

if (rootElement) {
	const root = createRoot(rootElement);
	const render = (Component: React.FC) => {
		root.render(
			<Provider store={store}>
				<I18nextProvider i18n={i18n}>
					<PersistGate loading={null} persistor={persistor}>
						<MsalProvider instance={msalInstance}>
							<React.StrictMode>
								<Component />
							</React.StrictMode>
						</MsalProvider>
					</PersistGate>
				</I18nextProvider>
			</Provider>
		);
	};

	render(App);

	if (module.hot) {
		module.hot.accept("./App", () => {
			const NextApp = require("./App").default;
			render(NextApp);
		});
	}
} else {
	console.error("Root element not found");
}
